import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Navbar from '../navbar/Navbar';
import Footer from '../footer/Footer';
import { useState } from "react";

const FILE_SIZE_300KB = 300 * 1024;
const FILE_SIZE_200KB = 200 * 1024;
const SUPPORTED_IMAGE_FORMATS = ["image/jpeg", "image/jpg"];
const SUPPORTED_PDF_FORMATS = ["application/pdf"];

const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
    dob: Yup.string().required("Required"),
    fatherName: Yup.string().required("Required"),
    motherName: Yup.string().required("Required"),
    address1: Yup.string().required("Required"),
    city: Yup.string().required("Required"),
    state: Yup.string().required("Required"),
    zip: Yup.string()
        .required("ZIP code is required")
        .matches(/^[1-9][0-9]{5}$/, "ZIP code must be a valid 6-digit number"),
    country: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    confirmEmail: Yup.string().oneOf([Yup.ref("email"), null], "Emails must match").required("Required"),
    aadhar: Yup.string()
        .required("Aadhar number is required")
        .matches(/^[2-9]{1}[0-9]{11}$/, "Aadhar must be a 12-digit valid number"),
    mobile: Yup.string()
        .required("Mobile number is required")
        .matches(/^[6-9]{1}[0-9]{9}$/, "Mobile must be a valid 10-digit number"),
    board: Yup.string().required("Required"),
    passYear: Yup.string().required("Required"),
    passMonth: Yup.string().required("Required"),
    totalMarks: Yup.string().required("Required"),
    groupMarks: Yup.string().required("Required"),
    preferences: Yup.array().of(Yup.string().required("Required")),
    qualifyingExam: Yup.string().required("Required"),
    jeeMainRank: Yup.string().nullable(),
    jeeMainPercentile: Yup.string().nullable(),
    tgeapcetRank: Yup.string().nullable(),
    declaration: Yup.boolean().oneOf([true], "You must accept the declaration to submit the form").required("Required"),
    files: Yup.object().shape({
        photo: Yup.mixed()
            .nullable()
            .test("fileSize", "File too large (Max 300KB)", value => !value || value.size <= FILE_SIZE_300KB)
            .test("fileType", "Unsupported file format (Only JPG/JPEG)", value =>
                !value || SUPPORTED_IMAGE_FORMATS.includes(value.type)
            ),
        sscMemo: Yup.mixed()
            .nullable()
            .test("fileSize", "File too large (Max 300KB)", value => !value || value.size <= FILE_SIZE_300KB)
            .test("fileType", "Unsupported file format (Only PDF)", value =>
                !value || SUPPORTED_PDF_FORMATS.includes(value.type)
            ),
        interMemo: Yup.mixed()
            .nullable()
            .test("fileSize", "File too large (Max 300KB)", value => !value || value.size <= FILE_SIZE_300KB)
            .test("fileType", "Unsupported file format (Only PDF)", value =>
                !value || SUPPORTED_PDF_FORMATS.includes(value.type)
            ),
        jeeRankCard: Yup.mixed()
            .nullable()
            .test("fileSize", "File too large (Max 300KB)", value => !value || value.size <= FILE_SIZE_300KB)
            .test("fileType", "Unsupported file format (Only PDF)", value =>
                !value || SUPPORTED_PDF_FORMATS.includes(value.type)
            ),
        tgeapcetRankCard: Yup.mixed()
            .nullable()
            .test("fileSize", "File too large (Max 300KB)", value => !value || value.size <= FILE_SIZE_300KB)
            .test("fileType", "Unsupported file format (Only PDF)", value =>
                !value || SUPPORTED_PDF_FORMATS.includes(value.type)
            ),
        applicantSignature: Yup.mixed()
            .nullable()
            .test("fileSize", "File too large (Max 200KB)", value => !value || value.size <= FILE_SIZE_200KB)
            .test("fileType", "Unsupported file format (Only JPG/JPEG)", value =>
                !value || SUPPORTED_IMAGE_FORMATS.includes(value.type)
            ),
        parentSignature: Yup.mixed()
            .nullable()
            .test("fileSize", "File too large (Max 200KB)", value => !value || value.size <= FILE_SIZE_200KB)
            .test("fileType", "Unsupported file format (Only JPG/JPEG)", value =>
                !value || SUPPORTED_IMAGE_FORMATS.includes(value.type)
            ),
    }),
});

const initialValues = {
    name: "",
    dob: "",
    fatherName: "",
    motherName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "India",
    email: "",
    confirmEmail: "",
    aadhar: "",
    mobile: "",
    board: "",
    passYear: "",
    passMonth: "",
    totalMarks: "",
    groupMarks: "",
    preferences: ["", "", "", "", ""],
    qualifyingExam: "",
    jeeMainRank: '',
    jeeMainPercentile: '',
    tgeapcetRank: '',
    submittedDate: new Date().toISOString().split("T")[0],
    files: {
        photo: null,
        sscMemo: null,
        interMemo: null,
        jeeRankCard: null,
        tgeapcetRankCard: null,
        applicantSignature: null,
        parentSignature: null,
    },
    status: "Pending",
    reason: "reason",
};

const BTechCategoryBFormJEE2026 = () => {
    const [fileNames, setFileNames] = useState({});

    const handleSubmit = async (values, { resetForm }) => {
        const formData = new FormData();
        const months = {
            "01": "January",
            "02": "February",
            "03": "March",
            "04": "April",
            "05": "May",
            "06": "June",
            "07": "July",
            "08": "August",
            "09": "September",
            "10": "October",
            "11": "November",
            "12": "December",
        };

        const passYear = `${months[values.passMonth]}, ${values.passYear}`;
        // Append all required fields
        formData.append("name", values.name || "");
        formData.append("dob", values.dob || "");
        formData.append("fatherName", values.fatherName || "");
        formData.append("motherName", values.motherName || "");
        formData.append("address1", values.address1 || "");
        formData.append("address2", values.address2 || "");
        formData.append("city", values.city || "");
        formData.append("state", values.state || "");
        formData.append("zip", values.zip || "");
        formData.append("country", values.country || "");
        formData.append("email", values.email || "");
        formData.append("confirmEmail", values.confirmEmail || "");
        formData.append("aadhar", values.aadhar || "");
        formData.append("mobile", values.mobile || "");
        formData.append("board", values.board || "");
        formData.append("passYear", passYear);
        formData.append("totalMarks", values.totalMarks || "");
        formData.append("groupMarks", values.groupMarks || "");
        formData.append("qualifyingExam", values.qualifyingExam || "");
        formData.append("jeeMainRank", values.jeeMainRank || "");
        formData.append("jeeMainPercentile", values.jeeMainPercentile || "");
        formData.append("tgeapcetRank", values.tgeapcetRank || "");
        formData.append("submittedDate", values.submittedDate || "");
        formData.append("status", values.status || "");
        formData.append("reason", values.reason || "");

        // Preferences: join as comma-separated string
        if (Array.isArray(values.preferences)) {
            formData.append("preferences", values.preferences.filter(Boolean).join(", "));
        }

        // Declaration (boolean)
        formData.append("declaration", values.declaration ? "true" : "false");

        // Append files (must be File objects)
        if (values.files) {
            Object.entries(values.files).forEach(([key, file]) => {
                if (file instanceof File) {
                    formData.append(key, file);
                }
            });
        }

        // Debug: Show all FormData entries
        console.log("FormData entries:");
        for (let [key, val] of formData.entries()) {
            console.log(`${key}:`, val);
        }

        try {
            const response = await axios.post(
                "https://admissions.swec.ac.in/api/jee/jee-admission",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            alert("Form submitted successfully!");
            console.log("Success:", response.data);
            resetForm();
            setFileNames({});
        } catch (error) {
            alert("Submission failed. Please try again.");
            console.error("AxiosError:", error);
        }
    };

    return (
        <>
            <Navbar />
            <div className="graduate-admission pb-70">
                <div className="container">
                    <div className="card p-2 p-lg-5 mt-5 mb-5">
                        <h2 className="mb-4 h4">
                            B.Tech Admission Form (2026-27) - Category B (JEE Main / TG EAPCET Qualified Examination)
                        </h2>
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ setFieldValue, isSubmitting, errors, touched, submitCount }) => (
                                <Form>
                                    <h5>1. Personal Details</h5>
                                    <div className="row m-0 p-0">
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    id="name"
                                                    name="name"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Full Name as per SSC"
                                                />
                                                <label className="text-muted" htmlFor="name">
                                                    Full Name as per SSC <span className="text-danger">*</span>
                                                </label>
                                                <ErrorMessage name="name" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>

                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    id="dob"
                                                    name="dob"
                                                    type="date"
                                                    className="form-control"
                                                    placeholder="Date of Birth"
                                                />
                                                <label className="text-muted" htmlFor="dob">
                                                    Date of Birth <span className="text-danger">*</span>
                                                </label>
                                                <ErrorMessage name="dob" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>

                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    id="fatherName"
                                                    name="fatherName"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Father Name"
                                                />
                                                <label className="text-muted" htmlFor="fatherName">
                                                    Father Name <span className="text-danger">*</span>
                                                </label>
                                                <ErrorMessage name="fatherName" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>

                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    id="motherName"
                                                    name="motherName"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Mother Name"
                                                />
                                                <label className="text-muted" htmlFor="motherName">
                                                    Mother Name <span className="text-danger">*</span>
                                                </label>
                                                <ErrorMessage name="motherName" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <h5 className="mt-3">2. Communication Address</h5>
                                    <div className="row m-0 p-0">
                                        {["address1", "address2", "city", "state", "zip"].map((field) => (
                                            <div className="col-md-6 px-3" key={field}>
                                                <div className="form-floating mb-3">
                                                    <Field
                                                        id={field}
                                                        name={field}
                                                        className="form-control"
                                                        placeholder={field}
                                                    />
                                                    <label className="text-muted" htmlFor={field}>
                                                        {field.replace(/([A-Z])/g, ' $1').replace(/^\w/, c => c.toUpperCase())}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <ErrorMessage name={field} component="div" className="text-danger mt-1" />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field as="select" id="country" name="country" className="form-select">
                                                    <option value="India">India</option>
                                                    <option value="Other">Other</option>
                                                </Field>
                                                <label className="text-muted" htmlFor="country">Country <span className="text-danger">*</span></label>
                                                <ErrorMessage name="country" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <h5 className="mt-3">3. Contact Details</h5>
                                    <div className="row m-0 p-0">
                                        {["email", "confirmEmail"].map((field) => (
                                            <div className="col-md-6 px-3" key={field}>
                                                <div className="form-floating mb-3">
                                                    <Field
                                                        id={field}
                                                        name={field}
                                                        type="email"
                                                        className="form-control"
                                                        placeholder={field}
                                                    />
                                                    <label className="text-muted" htmlFor={field}>
                                                        {field.replace(/([A-Z])/g, ' $1').replace(/^\w/, c => c.toUpperCase())}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <ErrorMessage name={field} component="div" className="text-danger mt-1" />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field id="aadhar" name="aadhar" className="form-control" placeholder="aadhar Number" />
                                                <label className="text-muted" htmlFor="aadhar">Aadhar Number <span className="text-danger">*</span></label>
                                                <ErrorMessage name="aadhar" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field id="mobile" name="mobile" className="form-control" placeholder="Mobile Number" />
                                                <label className="text-muted" htmlFor="mobile">Mobile Number <span className="text-danger">*</span></label>
                                                <ErrorMessage name="mobile" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <h5 className="mt-3">4. Academic Qualifications</h5>
                                    <div className="row m-0 p-0">
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    id="board"
                                                    name="board"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Name of the Board 10th + 2"
                                                />
                                                <label className="text-muted" htmlFor="board">
                                                    Name of the Board 10th + 2
                                                    <span className="text-danger">*</span>
                                                </label>
                                                <ErrorMessage name="board" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>

                                        <div className="col-md-6 px-3 row">
                                            <label className="form-label small mb-0" htmlFor="passMonth">
                                                Month and Year of passing <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-6 px-3 pt-0">
                                                <div className="mb-3">
                                                    <Field
                                                        as="select"
                                                        name="passMonth"
                                                        id="passMonth"
                                                        className="form-select"
                                                    >
                                                        <option value="">Select Month</option>
                                                        <option value="01">January</option>
                                                        <option value="02">February</option>
                                                        <option value="03">March</option>
                                                        <option value="04">April</option>
                                                        <option value="05">May</option>
                                                        <option value="06">June</option>
                                                        <option value="07">July</option>
                                                        <option value="08">August</option>
                                                        <option value="09">September</option>
                                                        <option value="10">October</option>
                                                        <option value="11">November</option>
                                                        <option value="12">December</option>
                                                    </Field>
                                                    <ErrorMessage name="passMonth" component="div" className="text-danger mt-1" />
                                                </div>
                                            </div>

                                            <div className="col-md-6 px-3 pt-0">
                                                <div className="mb-3">
                                                    <Field
                                                        as="select"
                                                        name="passYear"
                                                        id="passYear"
                                                        className="form-select"
                                                    >
                                                        <option value="">Select Year</option>
                                                        {Array.from({ length: 20 }, (_, i) => {
                                                            const year = new Date().getFullYear() - i;
                                                            return (
                                                                <option key={year} value={year}>
                                                                    {year}
                                                                </option>
                                                            );
                                                        })}
                                                    </Field>
                                                    <ErrorMessage name="passYear" component="div" className="text-danger mt-1" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    id="totalMarks"
                                                    name="totalMarks"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Total Marks"
                                                />
                                                <label className="text-muted" htmlFor="totalMarks">
                                                    Total Marks <span className="text-danger">*</span>
                                                </label>
                                                <ErrorMessage name="totalMarks" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>

                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    id="groupMarks"
                                                    name="groupMarks"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Group Marks"
                                                />
                                                <label className="text-muted" htmlFor="groupMarks">
                                                    Group Marks <span className="text-danger">*</span>
                                                </label>
                                                <ErrorMessage name="groupMarks" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                        <div className="col-md-6 px-3">
                                            <div className="mb-3">
                                                <label className="form-label">Qualifying Examination Passed <span className="text-danger">*</span></label>
                                                <div role="group" className="d-flex gap-3">
                                                    {["Intermediate", "CBSE", "ICSE", "Other"].map((val) => (
                                                        <label key={val} className="form-check-label text-muted">
                                                            <Field type="radio" name="qualifyingExam" value={val} className="form-check-input me-1" /> {val}
                                                        </label>
                                                    ))}
                                                </div>
                                                <ErrorMessage name="qualifyingExam" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <h5 className="mt-3">5. Course Preferences</h5>
                                    <div className="row m-0 p-0">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <div className="col-md-6 px-3" key={`preferences[${i}]`}>
                                                <div className="form-floating mb-3">
                                                    <Field
                                                        as="select"
                                                        id={`preferences[${i}]`}
                                                        name={`preferences[${i}]`}
                                                        className="form-select text-muted"
                                                    >
                                                        <option value="" disabled hidden></option>
                                                        <option value="CSE">CSE</option>
                                                        <option value="CSE (AI & ML)">CSE (AI & ML)</option>
                                                        <option value="CSE (Data Science)">CSE (Data Science)</option>
                                                        <option value="ECE">ECE</option>
                                                        <option value="IT">IT</option>
                                                    </Field>
                                                    <label htmlFor={`preferences[${i}]`}>Preference {i + 1} <span className="text-danger">*</span></label>
                                                    <ErrorMessage name={`preferences[${i}]`} component="div" className="text-danger mt-1" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <h5 className="mt-3">6. Entrance examination details</h5>
                                    <div className="row m-0 p-0">
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    type="text"
                                                    name="jeeMainRank"
                                                    className="form-control"
                                                    id="jeeMainRank"
                                                    placeholder="All India Rank in JEE (Main) - 2026"
                                                />
                                                <label className="text-muted" htmlFor="jeeMainRank">
                                                    All India Rank in JEE (Main) - 2026
                                                </label>
                                                <ErrorMessage name="jeeMainRank" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    type="text"
                                                    name="jeeMainPercentile"
                                                    className="form-control"
                                                    id="jeeMainPercentile"
                                                    placeholder="Percentile in JEE (Main) - 2026"
                                                />
                                                <label className="text-muted" htmlFor="jeeMainPercentile">
                                                    Percentile in JEE (Main) - 2026
                                                </label>
                                                <ErrorMessage name="jeeMainPercentile" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                        <div className="col-md-6 px-3">
                                            <div className="form-floating mb-3">
                                                <Field
                                                    type="text"
                                                    name="tgeapcetRank"
                                                    className="form-control"
                                                    id="tgeapcetRank"
                                                    placeholder="Rank in TGEAPCET - 2026"
                                                />
                                                <label className="text-muted" htmlFor="tgeapcetRank">
                                                    Rank in TGEAPCET - 2026
                                                </label>
                                                <ErrorMessage name="tgeapcetRank" component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <h5 className="mt-3">7. Document Uploads</h5>
                                    <div className="row m-0 p-0">
                                        {[
                                            { name: "photo", label: "Latest Passport Size Colour Photograph", type: "image", maxSize: 300 },
                                            { name: "sscMemo", label: "10th Marks Memo", type: "pdf", maxSize: 300 },
                                            { name: "interMemo", label: "Intermediate / 12th Marks Memo", type: "pdf", maxSize: 300 },
                                            { name: "jeeRankCard", label: "JEE Mains 2026 Rank Card", type: "pdf", maxSize: 300 },
                                            { name: "tgeapcetRankCard", label: "TGEAPCET 2026 Rank Card (optional)", type: "pdf", maxSize: 300 },
                                            { name: "applicantSignature", label: "Scanned Signature of Applicant", type: "image", maxSize: 200 },
                                            { name: "parentSignature", label: "Scanned Signature of Parent", type: "image", maxSize: 200 },
                                        ].map(({ name, label, type, maxSize }) => {
                                            const isImage = type === "image";
                                            return (
                                                <div className="col-md-6 px-3 mb-4" key={name}>
                                                    <label htmlFor={name} className="form-label">
                                                        {label}
                                                    </label>
                                                    <div
                                                        className="dropzone border rounded drag-drop-area d-flex align-items-center justify-content-center text-center p-4"
                                                        onClick={() => document.getElementById(name).click()}
                                                        style={{ cursor: "pointer", backgroundColor: "#fafafa" }}
                                                    >
                                                        <div>
                                                            {fileNames[name] ? (
                                                                <>
                                                                    <p className="mb-1 fw-bold text-success">{fileNames[name]}</p>
                                                                    <p className="text-muted small mb-0">File selected</p>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <p className="mb-1 text-muted"><strong>Click or Drag & Drop file here</strong></p>
                                                                    <small className="text-muted small mb-0">
                                                                        {isImage ? "JPG, JPEG" : "PDF"} only, Max size: {maxSize} KB
                                                                    </small>
                                                                </>
                                                            )}
                                                        </div>
                                                        <input
                                                            id={name}
                                                            name={`files.${name}`}
                                                            type="file"
                                                            className="form-control d-none"
                                                            accept={isImage ? "image/jpeg,image/jpg" : "application/pdf"}
                                                            onChange={(event) => {
                                                                const file = event.currentTarget.files[0];
                                                                if (file && file.size > maxSize * 1024) {
                                                                    alert(`File size should be less than ${maxSize}KB.`);
                                                                    event.target.value = "";
                                                                    setFileNames((prev) => ({ ...prev, [name]: "" }));
                                                                    setFieldValue(`files.${name}`, null);
                                                                } else {
                                                                    setFieldValue(`files.${name}`, file);
                                                                    setFileNames((prev) => ({ ...prev, [name]: file?.name || "" }));
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <ErrorMessage name={`files.${name}`} component="div" className="text-danger mt-1" />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-4 border-top pt-4">
                                        <h5 className="mb-3">Declaration</h5>
                                        <div className="form-check mb-3">
                                            <Field
                                                type="checkbox"
                                                name="declaration"
                                                className="form-check-input"
                                                id="declaration"
                                            />
                                            <label className="form-check-label" htmlFor="declaration">
                                                We hereby declare that all the information furnished above is true to the best of our knowledge. We are aware and give you an undertaking that our application form can summarily be rejected if any information provided is wrong.
                                            </label>
                                            <ErrorMessage
                                                name="declaration"
                                                component="div"
                                                className="text-danger mt-1"
                                            />
                                        </div>
                                    </div>
                                    {submitCount > 0 && Object.keys(errors).length > 0 && (
                                        <div className="alert alert-danger">
                                            {Object.keys(errors).join(", ")} Required
                                        </div>
                                    )}
                                    <div className="mt-4 d-flex justify-content-center">
                                        <button type='submit' className="btn btn-primary bg-gradient ">
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                    Submitting...
                                                </>
                                            ) : (
                                                <span>Submit</span>
                                            )}
                                        </button>
                                        <button type='reset' className="btn btn bg-danger bg-gradient ms-2 text-white">
                                            <span>Clear Form</span>
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default BTechCategoryBFormJEE2026;